<template>
    <div class="vue-map-container vue-map-osm" :class="{ 'with-message': messagePanelAvailable }">
        <div class="vue-map--wrapper">
            <img class="selected-position" :src="selectPositionIcon" v-if="allowToSelectPosition && locationEnabled" />
            <div ref="mapContainer" style="height: 100%; width: 100%"></div>
        </div>
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import bus from '@/services/bus-event';
import { debounce } from '@/services/utility';
import { TRIP_STATE } from '@/store/mutation-types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

export default {
    name: 'vue-maps-osm',
    props: {
        points: {
            type: Array,
            required: false,
            default: () => {
                return [];
            }
        },
        notCenteredPoints: {
            type: Array,
            required: false,
            default: () => {
                return [];
            }
        },
        center: {
            required: false,
            default: () => {
                return {
                    lat: 32,
                    lng: 64
                };
            }
        },
        zoom: {
            type: Number,
            required: false,
            default: 15
        },
        allowToSelectPosition: {
            type: Boolean,
            required: false,
            default: false
        },
        selectPositionIcon: {
            required: false,
            default: null
        },
        drawRouteData: {
            required: false,
            default: null
        }
    },
    data () {
        return {
            map: null,
            markers: [],
            routeLayer: null,
            routeDrawed: false,
            mapIsMoving: false,
            mapReCenterTimer: null,
            mapMoved: false,
            moveTimer: null,
            debounceFuncListener: null,
            isProgrammaticMove: false
        };
    },
    computed: {
        ...mapGetters({
            'isChofer': 'auth/isChofer',
            'locationEnabled': 'geolocation/locationEnabled',
            'settings': 'company/config',
            'currentTrip': 'auth/currentTrip'
        }),
        pointsOnMap () {
            let pointOfMap = this.allowToSelectPosition ? [] : this.points;
            if (this.notCenteredPoints && this.notCenteredPoints.length) {
                pointOfMap = [...pointOfMap, ...this.notCenteredPoints];
            }
            return pointOfMap;
        },
        centerOfTheMap () {
            if (this.points && this.points.length && this.points[0].location !== null) {
                return this.points[0].location;
            } else {
                if (this.center && this.center.lat) {
                    return this.center;
                } else {
                    return {
                        lat: 32,
                        lng: 64
                    };
                }
            }
        },
        messagePanelAvailable () {
            console.log('messagePanelAvailable', this.settings && this.settings.modulo_mensajes && this.currentTrip && this.currentTrip.frontState !== TRIP_STATE.PENDING);
            return this.settings && this.settings.modulo_mensajes && this.currentTrip && this.currentTrip.frontState !== TRIP_STATE.PENDING;
        }
    },
    mounted () {
        this.language = this.$getLang();
        bus.on('set_center', this.setCenter);
        bus.on('cordova_resume', this.cordova_resume);
        // Add class to body to help with z-index styling
        document.body.classList.add('has-osm-map');
        this.$nextTick(() => {
            this.renderMap();
        });
    },
    methods: {
        emitLocation () {
            // Don't emit location if the map move was programmatic (not user-initiated)
            if (this.isProgrammaticMove) {
                return;
            }
            if (this.map) {
                let center = this.map.getCenter();
                let emitPoint = {
                    location: {
                        lat: center.lat,
                        lng: center.lng
                    }
                };
                bus.emit('map_point_selected', emitPoint);
            }
        },
        handleMoveMap () {
            this.mapIsMoving = true;
            if (this.mapReCenterTimer) {
                window.clearTimeout(this.mapReCenterTimer);
            }
            this.mapReCenterTimer = window.setTimeout(() => {
                this.mapIsMoving = false;
            }, 50 * 1000);
        },
        zoomChange (newZoom) {
            this.handleMoveMap();
            this.mapMoved = true;
            if (this.moveTimer) {
                clearTimeout(this.moveTimer);
            }
            this.moveTimer = setTimeout(() => {
                this.mapMoved = false;
            }, 60 * 1000);
            this.$emit('zoom_changed', newZoom);
        },
        setCenter (location) {
            console.log('map setCenter', location);
            if (this.map && location && location.lat !== undefined && location.lng !== undefined) {
                // Reset mapIsMoving flag to allow forced center update
                // This ensures customPosition updates work even if map was recently moved
                this.mapIsMoving = false;
                if (this.mapReCenterTimer) {
                    window.clearTimeout(this.mapReCenterTimer);
                    this.mapReCenterTimer = null;
                }
                // Set flag to prevent emitLocation from firing during programmatic move
                this.isProgrammaticMove = true;
                // Force update the map center
                this.map.setView([location.lat, location.lng], 15, { animate: false });
                // Reset flag after a longer delay to ensure moveend event is processed
                // Leaflet's moveend can fire with some delay, so we need more time
                setTimeout(() => {
                    this.isProgrammaticMove = false;
                }, 500);
            }
        },
        dragStart (event) {
            this.handleMoveMap();
            this.$emit('drag_start', event);
        },
        dragEnd (event) {
            this.mapMoved = true;
            if (this.moveTimer) {
                clearTimeout(this.moveTimer);
            }
            this.moveTimer = setTimeout(() => {
                this.mapMoved = false;
            }, 60 * 1000);
            this.$emit('drag_end', event);
        },
        onClick (event) {
            this.$emit('map_click', event);
        },
        centerChange (newCenter) {
            if (newCenter !== null) {
                this.$emit('center_changed', { lat: newCenter.lat, lng: newCenter.lng });
            }
        },
        doFitBounds (points) {
            console.log('doFitBounds', points, this.mapMoved);
            if (this.mapMoved) {
                return;
            }
            if (points && points.length > 1) {
                if (this.map) {
                    let bounds = L.latLngBounds([]);
                    for (let i = 0; i < points.length; i++) {
                        bounds.extend([points[i].location.lat, points[i].location.lng]);
                    }

                    let modifier = {
                        north: 0.001,
                        est: 0.0005,
                        south: 0.001,
                        west: 0.0005
                    };
                    if (this.isChofer) {
                        modifier.south = 0.085;
                    } else {
                        modifier.north = 0.035;
                    }

                    // Extend bounds with modifiers
                    let ne = bounds.getNorthEast();
                    let sw = bounds.getSouthWest();
                    bounds.extend([ne.lat + modifier.north, ne.lng + modifier.est]);
                    bounds.extend([sw.lat - modifier.south, sw.lng - modifier.west]);

                    this.map.fitBounds(bounds);
                }
            } else if (points && points.length === 1) {
                this.setCenter(points[0].location);
            }
        },
        drawRoute () {
            if (!this.drawRouteData || !this.map) {
                return;
            }

            // Use OSRM to get route
            let origin = this.drawRouteData.origin;
            let destination = this.drawRouteData.destination;

            // Format coordinates for OSRM: lon,lat;lon,lat
            let coordinates = `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`;
            let url = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson`;

            fetch(url)
                .then(response => response.json())
                .then(data => {
                    if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
                        let route = data.routes[0];
                        let geometry = route.geometry;

                        // Remove existing route layer
                        if (this.routeLayer) {
                            this.map.removeLayer(this.routeLayer);
                        }

                        // Get main color from CSS
                        let maincolor = getComputedStyle(document.documentElement).getPropertyValue('--main-color') || '#5C1889';

                        // Create polyline from GeoJSON geometry
                        this.routeLayer = L.geoJSON(geometry, {
                            style: {
                                color: maincolor,
                                weight: 4,
                                opacity: 0.8
                            }
                        }).addTo(this.map);

                        this.routeDrawed = true;
                    } else {
                        console.error('OSRM route request failed');
                    }
                })
                .catch(error => {
                    console.error('Error fetching route:', error);
                });
        },
        renderMap () {
            if (!this.$refs.mapContainer) {
                return;
            }

            // Initialize map
            this.map = L.map(this.$refs.mapContainer, {
                zoomControl: true,
                attributionControl: true
            });

            // Add OpenStreetMap tile layer
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors',
                maxZoom: 19
            }).addTo(this.map);

            // Set initial view
            this.map.setView([this.centerOfTheMap.lat, this.centerOfTheMap.lng], this.zoom);

            // Fit bounds if we have points
            this.doFitBounds(this.points);

            // Add event listeners
            this.map.on('zoomend', () => {
                this.zoomChange(this.map.getZoom());
            });

            this.map.on('dragstart', (event) => {
                this.dragStart(event);
            });

            this.map.on('dragend', (event) => {
                this.dragEnd(event);
            });

            this.map.on('click', (event) => {
                this.onClick(event);
            });

            this.map.on('moveend', () => {
                let center = this.map.getCenter();
                this.centerChange(center);
            });

            // Handle position selection
            if (this.allowToSelectPosition) {
                let debounceFunc = debounce(this.emitLocation, 2000, false);
                this.map.on('moveend', debounceFunc);
                this.debounceFuncListener = debounceFunc;
            }

            // Draw route if needed
            if (this.settings && this.settings.modulo_sugerir_ruta_origen_destino && this.drawRouteData) {
                this.drawRoute();
            }

            // Update markers
            this.updateMarkers();
        },
        updateMarkers () {
            if (!this.map) {
                return;
            }

            // Remove existing markers
            this.markers.forEach(marker => {
                this.map.removeLayer(marker);
            });
            this.markers = [];

            // Add new markers
            this.pointsOnMap.forEach((point, index) => {
                if (point.location && !point.invisible) {
                    let markerOptions = {
                        clickable: true
                    };

                    if (point.icon) {
                        // Custom icon
                        markerOptions.icon = L.icon({
                            iconUrl: point.icon,
                            iconSize: [32, 32],
                            iconAnchor: [16, 32]
                        });
                    }

                    let marker = L.marker([point.location.lat, point.location.lng], markerOptions);

                    if (point.click) {
                        marker.on('click', point.click);
                    }

                    marker.addTo(this.map);
                    this.markers.push(marker);
                }
            });
        },
        cordova_resume () {
            if (window.device && window.device.platform === 'ios') {
                // Fix for iOS map refresh
                this.refreshMap();
            }
        },
        refreshMap () {
            if (this.map) {
                let actualZoom = this.map.getZoom();
                this.map.setZoom(actualZoom - 1);
                setTimeout(() => {
                    console.log('setZoom at refreshMap');
                    this.map.setZoom(actualZoom);
                }, 300);
            }
        }
    },
    watch: {
        points (newValue, oldValue) {
            if (newValue && newValue.length > 0) {
                setTimeout(() => {
                    console.log('change in points', this.mapIsMoving);
                    if (!this.mapIsMoving) {
                        this.doFitBounds(this.points);
                    }
                }, 200);
            }
            this.updateMarkers();
        },
        pointsOnMap () {
            this.updateMarkers();
        },
        drawRouteData () {
            if (this.settings && this.settings.modulo_sugerir_ruta_origen_destino) {
                if (this.drawRouteData) {
                    this.drawRoute();
                } else if (this.routeDrawed && this.routeLayer) {
                    this.map.removeLayer(this.routeLayer);
                    this.routeLayer = null;
                    this.routeDrawed = false;
                }
            }
        }
    },
    beforeDestroy () {
        bus.off('set_center', this.setCenter);
        bus.off('cordova_resume', this.refreshMap);
        // Remove class from body
        document.body.classList.remove('has-osm-map');
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }
};
</script>

<style scoped>
    .vue-map-container {
        height: calc(100vh - var(--header-height) - var(--ios-pad));
        overflow: hidden;
        position: relative;
        z-index: 1;
    }
    .vue-map--wrapper {
        width: 100%;
        height: 100%;
        position: relative;
        z-index: 1;
    }
    img.selected-position {
        top: 50%;
        left: 50%;
        margin-top: -38px;
        margin-left: -10px;
        position: absolute;
        z-index: 1000;
        pointer-events: none;
    }
    @media screen and (max-height: 700px) {
        .vue-map--wrapper{
            height: 150%;
            overflow: hidden;
        }
        img.selected-position {
            top: 75%;
        }
    }
</style>





// WEBPACK FOOTER //
// src/components/elements/MapsOSM.vue